import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { ChevronDown, ChevronRight, Loader2, X } from "lucide-react";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/utils/cn";
import { grcKeys } from "@/services/grc";
import {
  useBusinessAdvisorLookup,
  softHoldKeys,
  useCreateSoftHold,
  useSoftHoldById,
  useSoftHoldCategories,
  useSoftHoldMeetingTypes,
  useSoftHoldSubCategories,
  useSummitMeetingLookup
} from "@/services/softHolds";
import type { CreateSoftHoldRequest } from "@/types/softHolds";

const createSoftHoldSchema = z
  .object({
    meetingName: z.string().min(1, "Meeting name is required"),
    meetingId: z
      .string()
      .trim()
      .refine((v) => v === "" || /^\d+$/.test(v), "Meeting ID must contain only numbers"),
    meetingType: z.string().min(1, "Meeting type is required"),
    category: z.string().optional(),
    subCategory: z.string().optional(),
    businessAdvisor: z.string().min(1, "Business advisor is required"),
    businessAdvisorEmail: z.string(),
    summitMeetingStart: z.string().optional(),
    summitMeetingEnd: z.string().optional(),
    notes: z.string().optional()
  })
  .refine(
    (value) => {
      if (!value.summitMeetingStart || !value.summitMeetingEnd) return true;
      return value.summitMeetingEnd >= value.summitMeetingStart;
    },
    {
      message: "Summit meeting end date must be on or after summit meeting start date",
      path: ["summitMeetingEnd"]
    }
  );

type CreateSoftHoldValues = z.infer<typeof createSoftHoldSchema>;

type BlockCountDay = {
  day: string;
  blockCount: number;
  isDeleted: boolean;
};

type RoomBlockOption = {
  id: number;
  startDate: string;
  endDate: string;
  meetingStartDate: string;
  meetingEndDate: string;
  estimatedAttendance: number;
  blockCountDayWise: BlockCountDay[];
};

type RoomBlockOptionError = {
  startDate?: string;
  endDate?: string;
  meetingEndDate?: string;
};

interface CreateSoftHoldDialogProps {
  month: number;
  year: number;
  softHoldId?: number;
  onClose: () => void;
  onCreated?: () => void;
}

export function CreateSoftHoldDialog({
  month,
  year,
  softHoldId,
  onClose,
  onCreated
}: CreateSoftHoldDialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const createSoftHold = useCreateSoftHold();
  const isDetailsMode = Boolean(softHoldId && softHoldId > 0);
  const [meetingLookupInput, setMeetingLookupInput] = useState("");
  const [showMeetingLookup, setShowMeetingLookup] = useState(false);
  const [meetingLookupError, setMeetingLookupError] = useState<string | null>(null);
  const [isMeetingLinkedToSummit, setIsMeetingLinkedToSummit] = useState(false);
  const [businessAdvisorLookupInput, setBusinessAdvisorLookupInput] = useState("");
  const [showBusinessAdvisorLookup, setShowBusinessAdvisorLookup] = useState(false);
  const [roomBlockOptions, setRoomBlockOptions] = useState<RoomBlockOption[]>([
    {
      id: 1,
      startDate: "",
      endDate: "",
      meetingStartDate: "",
      meetingEndDate: "",
      estimatedAttendance: 0,
      blockCountDayWise: []
    }
  ]);
  const [nextRoomBlockOptionId, setNextRoomBlockOptionId] = useState(2);
  const [expandedOptionId, setExpandedOptionId] = useState<number | null>(1);
  const [primaryOptionId, setPrimaryOptionId] = useState<number>(1);
  const [roomBlockOptionErrors, setRoomBlockOptionErrors] = useState<Record<number, RoomBlockOptionError>>({});
  const isLinkedMode = isDetailsMode || isMeetingLinkedToSummit;
  const { data: softHoldDetails, isPending: isSoftHoldDetailsPending } = useSoftHoldById(
    softHoldId ?? 0,
    isDetailsMode
  );
  const { data: summitMeetingResults = [], isPending: isSummitLookupPending } = useSummitMeetingLookup(
    meetingLookupInput,
    !isDetailsMode && showMeetingLookup && /^\d+$/.test(meetingLookupInput.trim())
  );
  const { data: businessAdvisorResults = [], isPending: isBusinessAdvisorLookupPending } = useBusinessAdvisorLookup(
    businessAdvisorLookupInput,
    !isDetailsMode && !isLinkedMode && showBusinessAdvisorLookup
  );

  useEffect(() => {
    const firstFocusable = panelRef.current?.querySelector<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();
  }, []);

  function handlePanelKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      onClose();
    }
  }

  const form = useForm<CreateSoftHoldValues>({
    resolver: zodResolver(createSoftHoldSchema),
    defaultValues: {
      meetingName: "",
      meetingId: "",
      meetingType: "",
      category: "",
      subCategory: "",
      businessAdvisor: "",
      businessAdvisorEmail: "",
      summitMeetingStart: "",
      summitMeetingEnd: "",
      notes: ""
    }
  });

  const { data: meetingTypes = [], isPending: isMeetingTypesPending } = useSoftHoldMeetingTypes();
  const selectedMeetingTypeName = form.watch("meetingType");
  const selectedCategoryName = form.watch("category");

  const selectedMeetingTypeId = useMemo(() => {
    const selectedMeetingType = meetingTypes.find((item) => item.name === selectedMeetingTypeName);
    return selectedMeetingType?.id ?? null;
  }, [meetingTypes, selectedMeetingTypeName]);

  const { data: categories = [] } = useSoftHoldCategories(selectedMeetingTypeId);

  const selectedCategoryId = useMemo(() => {
    const selectedCategory = categories.find((item) => item.category === selectedCategoryName);
    return selectedCategory?.id ?? null;
  }, [categories, selectedCategoryName]);

  const { data: subCategories = [] } = useSoftHoldSubCategories(selectedCategoryId);

  useEffect(() => {
    if (!isDetailsMode || !softHoldDetails) return;

    form.reset({
      meetingName: softHoldDetails.meetingName ?? "",
      meetingId: softHoldDetails.meetingId ?? "",
      meetingType: softHoldDetails.meetingType ?? "",
      category: softHoldDetails.meetingCategory ?? "",
      subCategory: softHoldDetails.meetingSubCategory ?? "",
      businessAdvisor: softHoldDetails.businessAdvisor ?? "",
      businessAdvisorEmail: softHoldDetails.businessAdvisorEmail ?? "",
      summitMeetingStart: toDateInputValue(softHoldDetails.summitMeetingStart),
      summitMeetingEnd: toDateInputValue(softHoldDetails.summitMeetingEnd),
      notes: softHoldDetails.notes ?? ""
    });
    const mappedOptions =
      softHoldDetails.preferredDates.length > 0
        ? softHoldDetails.preferredDates.map((dateOption, index) => ({
            id: index + 1,
            startDate: toDateInputValue(dateOption.startDate),
            endDate: toDateInputValue(dateOption.endDate),
            meetingStartDate: toDateInputValue(dateOption.meetingStartDate),
            meetingEndDate: toDateInputValue(dateOption.meetingEndDate),
            estimatedAttendance: Math.max(0, dateOption.estimatedAttendance ?? 0),
            blockCountDayWise: (dateOption.blockCountDayWise ?? []).map((day) => ({
              day: toDateInputValue(day.day),
              blockCount: Math.max(0, Number(day.blockCount) || 0),
              isDeleted: Boolean(day.isDeleted)
            }))
          }))
        : [
            {
              id: 1,
              startDate: "",
              endDate: "",
              meetingStartDate: "",
              meetingEndDate: "",
              estimatedAttendance: 0,
              blockCountDayWise: []
            }
          ];

    setRoomBlockOptions(mappedOptions);
    setNextRoomBlockOptionId(mappedOptions.length + 1);
    setExpandedOptionId(mappedOptions[mappedOptions.length - 1]?.id ?? 1);
    setPrimaryOptionId(mappedOptions[0]?.id ?? 1);
    setRoomBlockOptionErrors({});
    setMeetingLookupInput(softHoldDetails.meetingId ?? "");
    setIsMeetingLinkedToSummit(Boolean(softHoldDetails.meetingId));
    setBusinessAdvisorLookupInput(softHoldDetails.businessAdvisorEmail ?? softHoldDetails.businessAdvisor ?? "");
  }, [form, isDetailsMode, softHoldDetails]);

  useEffect(() => {
    if (isDetailsMode) return;
    const currentMeetingId = form.watch("meetingId")?.trim() ?? "";
    if (!currentMeetingId) {
      setMeetingLookupInput("");
      setIsMeetingLinkedToSummit(false);
      setMeetingLookupError(null);
    }
  }, [form, isDetailsMode]);

  function handleMeetingLookupSelection(selectedMeetingId: string) {
    const selected = summitMeetingResults.find(
      (item) => (item.summitMeetingId ?? "") === selectedMeetingId
    );

    if (!selected) return;

    if (selected.errorMessage) {
      setMeetingLookupError(selected.errorMessage);
      return;
    }

    form.setValue("meetingId", selected.summitMeetingId ?? "");
    form.setValue("meetingName", selected.meetingName ?? "");
    form.setValue("meetingType", selected.meetingType ?? "");
    form.setValue("category", selected.category ?? "");
    form.setValue("subCategory", selected.subCategory ?? "");
    form.setValue("businessAdvisor", selected.businessAdvisorName ?? "");
    form.setValue("businessAdvisorEmail", selected.businessAdvisorEmail ?? "");
    form.setValue("summitMeetingStart", toDateInputValue(selected.meetingStartDate));
    form.setValue("summitMeetingEnd", toDateInputValue(selected.meetingEndDate));

    setMeetingLookupInput(selected.summitMeetingId ?? "");
    setBusinessAdvisorLookupInput(selected.businessAdvisorEmail ?? "");
    setMeetingLookupError(null);
    setIsMeetingLinkedToSummit(true);
    setShowMeetingLookup(false);
  }

  function handleBusinessAdvisorSelection(advisorEmail: string) {
    const selectedAdvisor = businessAdvisorResults.find((item) => item.email === advisorEmail);
    if (!selectedAdvisor) return;

    const displayName = selectedAdvisor.displayName || selectedAdvisor.fullName;
    form.setValue("businessAdvisor", displayName ?? "");
    form.setValue("businessAdvisorEmail", selectedAdvisor.email ?? "");
    setBusinessAdvisorLookupInput(selectedAdvisor.email ?? "");
    setShowBusinessAdvisorLookup(false);
  }

  function handleUnlinkSummitMeeting() {
    if (isDetailsMode) return;

    form.setValue("meetingId", "");
    setMeetingLookupInput("");
    setShowMeetingLookup(false);
    setMeetingLookupError(null);
    setIsMeetingLinkedToSummit(false);
  }

  function updateRoomBlockOption(
    optionId: number,
    updater: (previousOption: RoomBlockOption) => RoomBlockOption
  ) {
    setRoomBlockOptions((previousOptions) =>
      previousOptions.map((option) => (option.id === optionId ? updater(option) : option))
    );
  }

  function rebuildBlockDays(startDate: string, endDate: string, previousRows: BlockCountDay[]) {
    if (!startDate || !endDate || endDate < startDate) return [];
    const previousMap = new Map(previousRows.map((row) => [row.day, row]));
    const generatedDays = buildDayRange(startDate, endDate);

    return generatedDays.map((day) => {
      const existing = previousMap.get(day);
      return {
        day,
        blockCount: existing ? Math.max(0, existing.blockCount) : 0,
        isDeleted: false
      };
    });
  }

  function addRoomBlockOption() {
    if (isDetailsMode) return;
    const createdId = nextRoomBlockOptionId;
    setRoomBlockOptions((previousOptions) => [
      ...previousOptions,
      {
        id: createdId,
        startDate: "",
        endDate: "",
        meetingStartDate: "",
        meetingEndDate: "",
        estimatedAttendance: 0,
        blockCountDayWise: []
      }
    ]);
    setNextRoomBlockOptionId((current) => current + 1);
    setExpandedOptionId(createdId);
  }

  function removeRoomBlockOption(optionId: number) {
    if (isDetailsMode) return;
    setRoomBlockOptions((previousOptions) => {
      if (previousOptions.length <= 1) return previousOptions;

      const filtered = previousOptions.filter((option) => option.id !== optionId);
      if (expandedOptionId === optionId) {
        setExpandedOptionId(filtered[filtered.length - 1]?.id ?? null);
      }
      if (primaryOptionId === optionId) {
        setPrimaryOptionId(filtered[0]?.id ?? 1);
      }
      return filtered;
    });
    setRoomBlockOptionErrors((previousErrors) => {
      const nextErrors = { ...previousErrors };
      delete nextErrors[optionId];
      return nextErrors;
    });
  }

  async function onSubmit(values: CreateSoftHoldValues) {
    if (isDetailsMode) return;

    const validationErrors: Record<number, RoomBlockOptionError> = {};
    roomBlockOptions.forEach((option) => {
      const optionError: RoomBlockOptionError = {};
      if (!option.startDate) {
        optionError.startDate = "Room block start date is required";
      }
      if (!option.endDate) {
        optionError.endDate = "Room block end date is required";
      }
      if (option.startDate && option.endDate && option.endDate < option.startDate) {
        optionError.endDate = "Room block end date must be on or after start date";
      }
      if (
        option.meetingStartDate &&
        option.meetingEndDate &&
        option.meetingEndDate < option.meetingStartDate
      ) {
        optionError.meetingEndDate = "Meeting end date must be on or after meeting start date";
      }

      if (Object.keys(optionError).length > 0) {
        validationErrors[option.id] = optionError;
      }
    });

    if (roomBlockOptions.length < 1) {
      toast.error("At least one room block option is required.");
      return;
    }

    if (Object.keys(validationErrors).length > 0) {
      setRoomBlockOptionErrors(validationErrors);
      toast.error("Please fix errors in Dates and Room Blocks options.");
      return;
    }

    setRoomBlockOptionErrors({});

    const payload: CreateSoftHoldRequest = {
      meetingName: values.meetingName.trim(),
      meetingId: values.meetingId.trim() || undefined,
      meetingType: values.meetingType,
      organizerName: "",
      email: values.businessAdvisorEmail.trim() || undefined,
      status: "Soft Hold",
      isDeleted: false,
      notes: values.notes?.trim() || undefined,
      businessAdvisor: values.businessAdvisor.trim(),
      businessAdvisorEmail: values.businessAdvisorEmail.trim(),
      category: values.category || undefined,
      subCategory: values.subCategory || undefined,
      summitMeetingStart: values.summitMeetingStart || undefined,
      summitMeetingEnd: values.summitMeetingEnd || undefined,
      softHoldPreferredDates: roomBlockOptions.map((option, index) => ({
        startDate: option.startDate,
        endDate: option.endDate,
        meetingStartDate: option.meetingStartDate || option.startDate,
        meetingEndDate: option.meetingEndDate || option.endDate,
        status: "",
        isDeleted: false,
        isSelected: index === 0,
        isItPrimaryDate: index === 0,
        estimatedAttendance: Math.max(0, option.estimatedAttendance),
        blockCountDayWise:
          option.blockCountDayWise.length > 0
            ? option.blockCountDayWise.map((row) => ({
                day: row.day,
                blockCount: Math.max(0, row.blockCount),
                isDeleted: row.isDeleted
              }))
            : buildBlockCountDays(option.startDate, option.endDate)
      }))
    };

    try {
      const createdSoftHoldId = await createSoftHold.mutateAsync(payload);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: grcKeys.byMonth(month, year) }),
        queryClient.invalidateQueries({ queryKey: softHoldKeys.byMonth(month, year) }),
        createdSoftHoldId > 0
          ? queryClient.invalidateQueries({ queryKey: softHoldKeys.byId(createdSoftHoldId) })
          : Promise.resolve()
      ]);

      toast.success("Soft hold created successfully.");
      onCreated?.();
      onClose();
    } catch {
      toast.error("Unable to create soft hold. Please verify required fields and try again.");
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-[70] bg-black/30" onClick={onClose} aria-hidden="true" />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-soft-hold-title"
        tabIndex={-1}
        className="fixed inset-y-0 right-0 z-[80] ml-auto flex h-full w-full max-w-3xl flex-col border-l border-slate-300 bg-white shadow-xl"
        onKeyDown={handlePanelKeyDown}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 id="create-soft-hold-title" className="text-base font-semibold text-slate-900">
            {isDetailsMode ? "Soft Hold Details" : "Add Soft Hold"}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close create soft hold dialog">
            <X className="h-5 w-5 text-slate-500 hover:text-slate-900" />
          </button>
        </div>

        {isDetailsMode && isSoftHoldDetailsPending ? (
          <div className="flex flex-1 items-center justify-center text-sm text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading soft hold details...
          </div>
        ) : (
          <>
            <form
              id="create-soft-hold-form"
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex-1 space-y-5 overflow-y-auto px-4 py-4"
            >
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">General Information</h3>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label htmlFor="softhold-meeting-id" className="mb-1 block text-xs font-medium text-slate-700">
                  Summit Meeting ID
                </label>
                <div className="relative">
                  <input
                    id="softhold-meeting-id"
                    type="text"
                    value={meetingLookupInput}
                    onChange={(event) => {
                      const value = event.target.value;
                      setMeetingLookupInput(value);
                      form.setValue("meetingId", value);
                      setMeetingLookupError(null);
                      setShowMeetingLookup(Boolean(value.trim()));
                      if (!value.trim()) {
                        setIsMeetingLinkedToSummit(false);
                      }
                    }}
                    onFocus={() => {
                      if (!isDetailsMode && meetingLookupInput.trim()) {
                        setShowMeetingLookup(true);
                      }
                    }}
                    readOnly={isDetailsMode}
                    className={inputCls(!!form.formState.errors.meetingId)}
                    placeholder="Search Summit Meeting ID"
                  />

                  {showMeetingLookup && !isDetailsMode && (
                    <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded border border-slate-200 bg-white shadow-lg">
                      {!/^\d+$/.test(meetingLookupInput.trim()) ? (
                        <p className="px-3 py-2 text-xs text-slate-500">
                          Enter numeric Meeting ID to search Summit.
                        </p>
                      ) : isSummitLookupPending ? (
                        <p className="px-3 py-2 text-xs text-slate-500">Searching Summit...</p>
                      ) : summitMeetingResults.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-slate-500">No Summit meeting found.</p>
                      ) : (
                        summitMeetingResults.map((result) => (
                          <button
                            key={`${result.summitMeetingId}-${result.meetingName}`}
                            type="button"
                            onClick={() => handleMeetingLookupSelection(result.summitMeetingId ?? "")}
                            className="block w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
                          >
                            <span className="font-semibold">{result.summitMeetingId}</span>
                            <span className="ml-2">{result.meetingName}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {!isDetailsMode && isMeetingLinkedToSummit && (
                  <button
                    type="button"
                    onClick={handleUnlinkSummitMeeting}
                    className="mt-1 text-xs text-slate-600 underline hover:text-slate-900"
                  >
                    Unlink Summit Meeting
                  </button>
                )}

                {meetingLookupError ? (
                  <p className="mt-1 text-xs text-red-600">{meetingLookupError}</p>
                ) : (
                  form.formState.errors.meetingId?.message && (
                    <p className="mt-1 text-xs text-red-600">{form.formState.errors.meetingId?.message}</p>
                  )
                )}
              </div>

              <Field label="Business Advisor" htmlFor="softhold-business-advisor" required error={form.formState.errors.businessAdvisor?.message}>
                <div className="relative">
                  <input
                    id="softhold-business-advisor"
                    type="text"
                    value={businessAdvisorLookupInput}
                    onChange={(event) => {
                      const value = event.target.value;
                      setBusinessAdvisorLookupInput(value);
                      setShowBusinessAdvisorLookup(Boolean(value.trim()));
                      form.setValue("businessAdvisor", "");
                      form.setValue("businessAdvisorEmail", value.trim());
                    }}
                    onFocus={() => {
                      if (!isLinkedMode && businessAdvisorLookupInput.trim()) {
                        setShowBusinessAdvisorLookup(true);
                      }
                    }}
                    readOnly={isLinkedMode}
                    className={inputCls(!!form.formState.errors.businessAdvisor)}
                    placeholder="Search by advisor email"
                  />

                  {showBusinessAdvisorLookup && !isLinkedMode && (
                    <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded border border-slate-200 bg-white shadow-lg">
                      {isBusinessAdvisorLookupPending ? (
                        <p className="px-3 py-2 text-xs text-slate-500">Searching business advisors...</p>
                      ) : businessAdvisorResults.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-slate-500">No business advisors found.</p>
                      ) : (
                        businessAdvisorResults.map((advisor) => (
                          <button
                            key={`${advisor.id}-${advisor.email}`}
                            type="button"
                            onClick={() => handleBusinessAdvisorSelection(advisor.email)}
                            className="block w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
                          >
                            <span className="font-semibold">{advisor.email}</span>
                            <span className="ml-2">{advisor.displayName || advisor.fullName}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </Field>

              <Field label="Summit Meeting Start" htmlFor="softhold-meeting-start-date">
                <input
                  id="softhold-meeting-start-date"
                  type="date"
                  {...form.register("summitMeetingStart", {
                    onChange: (event) => {
                      const value = event.target.value;
                      const currentEnd = form.getValues("summitMeetingEnd");
                      if (!currentEnd || currentEnd < value) {
                        form.setValue("summitMeetingEnd", value);
                      }
                    }
                  })}
                  readOnly={isLinkedMode}
                  className={inputCls(false)}
                />
              </Field>

              <Field label="Summit Meeting End" htmlFor="softhold-meeting-end-date" error={form.formState.errors.summitMeetingEnd?.message}>
                <input
                  id="softhold-meeting-end-date"
                  type="date"
                  min={form.watch("summitMeetingStart") || undefined}
                  {...form.register("summitMeetingEnd")}
                  readOnly={isLinkedMode}
                  className={inputCls(!!form.formState.errors.summitMeetingEnd)}
                />
              </Field>

              <Field
                label="Meeting Name"
                htmlFor="softhold-meeting-name"
                required
                error={form.formState.errors.meetingName?.message}
              >
                <input
                  id="softhold-meeting-name"
                  type="text"
                  {...form.register("meetingName")}
                  readOnly={isDetailsMode}
                  className={inputCls(!!form.formState.errors.meetingName)}
                />
              </Field>

              <Field
                label="Meeting Type"
                htmlFor="softhold-meeting-type"
                required
                error={form.formState.errors.meetingType?.message}
              >
                <select
                  id="softhold-meeting-type"
                  {...form.register("meetingType", {
                    onChange: () => {
                      form.setValue("category", "");
                      form.setValue("subCategory", "");
                    }
                  })}
                  className={inputCls(!!form.formState.errors.meetingType)}
                  disabled={isMeetingTypesPending || isLinkedMode}
                >
                  <option value="">Select meeting type</option>
                  {meetingTypes.map((meetingType) => (
                    <option key={meetingType.id} value={meetingType.name}>
                      {meetingType.name}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Category" htmlFor="softhold-category">
                <select
                  id="softhold-category"
                  {...form.register("category", {
                    onChange: () => {
                      form.setValue("subCategory", "");
                    }
                  })}
                  className={inputCls(false)}
                  disabled={!selectedMeetingTypeId || isLinkedMode}
                >
                  <option value="">Select category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.category}>
                      {category.category}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Subcategory" htmlFor="softhold-sub-category">
                <select
                  id="softhold-sub-category"
                  {...form.register("subCategory")}
                  className={inputCls(false)}
                  disabled={!selectedCategoryId || isLinkedMode}
                >
                  <option value="">Select subcategory</option>
                  {subCategories.map((subCategory) => (
                    <option key={subCategory.id} value={subCategory.subCategory}>
                      {subCategory.subCategory}
                    </option>
                  ))}
                </select>
              </Field>

              <input type="hidden" {...form.register("businessAdvisorEmail")} />
            </div>
          </section>

          <section className="space-y-3 border-t border-slate-200 pt-4">
            <h3 className="text-sm font-semibold text-slate-900">Dates and Room Blocks</h3>
            <div className="space-y-4">
              {roomBlockOptions.map((option) => {
                const optionErrors = roomBlockOptionErrors[option.id] ?? {};
                const isExpanded = expandedOptionId === option.id;

                return (
                  <div key={option.id} className="rounded border border-slate-200 p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedOptionId((currentExpandedId) =>
                            currentExpandedId === option.id ? null : option.id
                          )
                        }
                        className="flex items-center gap-2 rounded px-1 py-1 text-left hover:bg-slate-50"
                      >
                        <input
                          type="checkbox"
                          checked={true}
                          readOnly={true}
                          disabled={true}
                          className="h-4 w-4 rounded border-slate-300 cursor-not-allowed"
                        />
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-slate-600" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-slate-600" />
                        )}
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <Field label="Room Block Start Date" htmlFor={`softhold-start-date-${option.id}`} required error={optionErrors.startDate}>
                        <input
                          id={`softhold-start-date-${option.id}`}
                          type="date"
                          value={option.startDate}
                          onChange={(event) => {
                            const value = event.target.value;
                            updateRoomBlockOption(option.id, (previousOption) => {
                              const nextEndDate =
                                !previousOption.endDate || previousOption.endDate < value
                                  ? value
                                  : previousOption.endDate;
                              return {
                                ...previousOption,
                                startDate: value,
                                endDate: nextEndDate,
                                blockCountDayWise: rebuildBlockDays(
                                  value,
                                  nextEndDate,
                                  previousOption.blockCountDayWise
                                )
                              };
                            });
                          }}
                          readOnly={isDetailsMode}
                          className={inputCls(Boolean(optionErrors.startDate))}
                        />
                      </Field>

                      <Field label="Room Block End Date" htmlFor={`softhold-end-date-${option.id}`} required error={optionErrors.endDate}>
                        <input
                          id={`softhold-end-date-${option.id}`}
                          type="date"
                          min={option.startDate || undefined}
                          value={option.endDate}
                          onChange={(event) => {
                            const value = event.target.value;
                            updateRoomBlockOption(option.id, (previousOption) => ({
                              ...previousOption,
                              endDate: value,
                              blockCountDayWise: rebuildBlockDays(
                                previousOption.startDate,
                                value,
                                previousOption.blockCountDayWise
                              )
                            }));
                          }}
                          readOnly={isDetailsMode}
                          className={inputCls(Boolean(optionErrors.endDate))}
                        />
                      </Field>

                      <Field label="Meeting Start Date" htmlFor={`softhold-room-meeting-start-date-${option.id}`}>
                        <input
                          id={`softhold-room-meeting-start-date-${option.id}`}
                          type="date"
                          value={option.meetingStartDate}
                          onChange={(event) => {
                            const value = event.target.value;
                            updateRoomBlockOption(option.id, (previousOption) => ({
                              ...previousOption,
                              meetingStartDate: value,
                              meetingEndDate:
                                !previousOption.meetingEndDate || previousOption.meetingEndDate < value
                                  ? value
                                  : previousOption.meetingEndDate
                            }));
                          }}
                          readOnly={isDetailsMode}
                          className={inputCls(false)}
                        />
                      </Field>

                      <Field label="Meeting End Date" htmlFor={`softhold-room-meeting-end-date-${option.id}`} error={optionErrors.meetingEndDate}>
                        <input
                          id={`softhold-room-meeting-end-date-${option.id}`}
                          type="date"
                          min={option.meetingStartDate || undefined}
                          value={option.meetingEndDate}
                          onChange={(event) => {
                            const value = event.target.value;
                            updateRoomBlockOption(option.id, (previousOption) => ({
                              ...previousOption,
                              meetingEndDate: value
                            }));
                          }}
                          readOnly={isDetailsMode}
                          className={inputCls(Boolean(optionErrors.meetingEndDate))}
                        />
                      </Field>

                      <Field label="Estimated Attendance" htmlFor={`softhold-estimated-attendance-${option.id}`}>
                        <input
                          id={`softhold-estimated-attendance-${option.id}`}
                          type="number"
                          min={0}
                          step={1}
                          value={option.estimatedAttendance}
                          onChange={(event) => {
                            const parsed = Number(event.target.value);
                            const nextValue = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
                            updateRoomBlockOption(option.id, (previousOption) => ({
                              ...previousOption,
                              estimatedAttendance: nextValue
                            }));
                          }}
                          readOnly={isDetailsMode}
                          className={inputCls(false)}
                        />
                      </Field>

                      {option.blockCountDayWise.length > 0 && (
                        <div className="md:col-span-2">
                          <label className="mb-1 block text-xs font-medium text-slate-700">Room Block Count by Day</label>
                          <div className="space-y-2 rounded border border-slate-200 p-3">
                            {option.blockCountDayWise.map((dayRow, dayIndex) => (
                              <div key={dayRow.day} className="grid grid-cols-12 items-center gap-2">
                                <div className="col-span-7 text-sm text-slate-700">
                                  {format(new Date(`${dayRow.day}T00:00:00`), "MM-dd-yyyy")}
                                </div>
                                <div className="col-span-5">
                                  <input
                                    type="number"
                                    min={0}
                                    step={1}
                                    value={dayRow.blockCount}
                                    onChange={(event) => {
                                      const parsed = Number(event.target.value);
                                      const nextCount = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
                                      updateRoomBlockOption(option.id, (previousOption) => ({
                                        ...previousOption,
                                        blockCountDayWise: previousOption.blockCountDayWise.map((row, rowIndex) =>
                                          rowIndex === dayIndex ? { ...row, blockCount: nextCount } : row
                                        )
                                      }));
                                    }}
                                    readOnly={isDetailsMode}
                                    className={inputCls(false)}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {!isDetailsMode && (
                        <div className="md:col-span-2 flex justify-end">
                          <button
                            type="button"
                            onClick={() => removeRoomBlockOption(option.id)}
                            disabled={roomBlockOptions.length <= 1}
                            className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                      </div>
                    )}
                  </div>
                );
              })}

              {!isDetailsMode && (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={addRoomBlockOption}
                    className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    Add Option
                  </button>
                </div>
              )}
            </div>
          </section>

          <section className="space-y-2 border-t border-slate-200 pt-4">
            <Field label="Notes" htmlFor="softhold-notes">
              <textarea
                id="softhold-notes"
                rows={4}
                {...form.register("notes")}
                readOnly={isDetailsMode}
                className={inputCls(false)}
              />
            </Field>
          </section>
            </form>

            <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
              <button
                type="button"
                onClick={onClose}
                disabled={createSoftHold.isPending}
                className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                {isDetailsMode ? "Close" : "Cancel"}
              </button>
              {!isDetailsMode && (
                <button
                  type="submit"
                  form="create-soft-hold-form"
                  disabled={createSoftHold.isPending}
                  className={cn(
                    "inline-flex items-center gap-2 rounded border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white",
                    "hover:bg-slate-800 disabled:opacity-60"
                  )}
                >
                  {createSoftHold.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function toDateInputValue(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function buildBlockCountDays(startDate: string, endDate: string) {
  const dates: Array<{ day: string; blockCount: number; isDeleted: boolean }> = [];
  if (!startDate || !endDate || endDate < startDate) return dates;

  const cursor = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);

  while (cursor <= end) {
    dates.push({
      day: cursor.toISOString(),
      blockCount: 0,
      isDeleted: false
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
}

function buildDayRange(startDate: string, endDate: string) {
  const dates: string[] = [];
  if (!startDate || !endDate || endDate < startDate) return dates;

  const cursor = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);

  while (cursor <= end) {
    dates.push(cursor.toISOString().slice(0, 10));
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
}

function Field({
  label,
  htmlFor,
  required,
  error,
  children
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  error?: string;
  children: import("react").ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1 block text-xs font-medium text-slate-700">
        {label}
        {required ? <span className="text-red-600">*</span> : null}
      </label>
      {children}
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
    </div>
  );
}

function inputCls(hasError: boolean) {
  return cn(
    "w-full rounded border px-3 py-2 text-sm",
    "border-slate-300 focus:border-slate-500 focus:outline-none focus:ring-0",
    hasError && "border-red-500"
  );
}
